import { crc32c } from './crc32c.js';

// This is an application envelope carried by a standard QR symbol. It is not
// presented as an IEEE PHY/MAC bitstream. The optical profile is selected in
// profiles.js; application interoperability stays explicit and versioned here.
export const ENVELOPE_PREFIX = 'LUMI1:';
export const BROADCAST_ADDRESS = 0xffff;
export const MAX_PAYLOAD_BYTES = 240;

export const FRAME_TYPE = Object.freeze({
  DATA: 1,
  ACK: 2,
  CALIBRATION: 3,
});

const MAGIC = Uint8Array.of(0x4c, 0x43, 0x48, 0x31); // LCH1
const HEADER_BYTES = 15;
const CRC_BYTES = 4;

export function encodeEnvelope({
  type = FRAME_TYPE.DATA,
  source,
  destination = BROADCAST_ADDRESS,
  sequence = 0,
  ackRequested = false,
  payload = '',
}) {
  const body = typeof payload === 'string' ? new TextEncoder().encode(payload) : payload;
  if (!(body instanceof Uint8Array)) throw new TypeError('payload doit être une chaîne ou Uint8Array');
  if (body.length > MAX_PAYLOAD_BYTES) throw new RangeError(`payload trop long: ${body.length} > ${MAX_PAYLOAD_BYTES}`);
  assertUint(type, 8, 'type');
  assertUint(source, 16, 'source');
  assertUint(destination, 16, 'destination');
  assertUint(sequence, 8, 'sequence');

  const bytes = new Uint8Array(HEADER_BYTES + body.length + CRC_BYTES);
  bytes.set(MAGIC, 0);
  bytes[4] = type;
  bytes[5] = ackRequested ? 1 : 0;
  bytes[6] = sequence;
  writeU16(bytes, 7, source);
  writeU16(bytes, 9, destination);
  writeU16(bytes, 11, body.length);
  // Bytes 13..14 are reserved and MUST stay zero for this envelope version.
  bytes.set(body, HEADER_BYTES);
  const checksum = crc32c(bytes.subarray(0, -CRC_BYTES));
  writeU32(bytes, bytes.length - CRC_BYTES, checksum);
  return {
    text: `${ENVELOPE_PREFIX}${toBase64Url(bytes)}`,
    bytes,
    checksum,
  };
}

export function decodeEnvelope(text) {
  if (typeof text !== 'string' || !text.startsWith(ENVELOPE_PREFIX)) {
    return reject('NOT_LUMICHAT', 'préfixe LumiChat absent');
  }
  let bytes;
  try {
    bytes = fromBase64Url(text.slice(ENVELOPE_PREFIX.length));
  } catch {
    return reject('BAD_BASE64', 'encodage base64url invalide');
  }
  if (bytes.length < HEADER_BYTES + CRC_BYTES) return reject('TRUNCATED', 'enveloppe tronquée');
  if (!MAGIC.every((value, index) => bytes[index] === value)) return reject('BAD_MAGIC', 'signature/version inconnue');
  if (bytes[13] !== 0 || bytes[14] !== 0) return reject('RESERVED_BITS', 'bits réservés non nuls');

  const payloadLength = readU16(bytes, 11);
  const expected = HEADER_BYTES + payloadLength + CRC_BYTES;
  if (bytes.length !== expected) return reject('BAD_LENGTH', `longueur ${bytes.length}, attendu ${expected}`);
  const receivedChecksum = readU32(bytes, bytes.length - CRC_BYTES);
  const computedChecksum = crc32c(bytes.subarray(0, -CRC_BYTES));
  if (receivedChecksum !== computedChecksum) {
    return reject('CRC32C', `CRC32C reçu ${hex(receivedChecksum)}, calculé ${hex(computedChecksum)}`);
  }

  const type = bytes[4];
  if (![FRAME_TYPE.DATA, FRAME_TYPE.ACK, FRAME_TYPE.CALIBRATION].includes(type)) {
    return reject('BAD_TYPE', `type réservé ${type}`);
  }
  const flags = bytes[5];
  if (flags & 0xfe) return reject('RESERVED_BITS', 'flags réservés non nuls');
  const payload = bytes.slice(HEADER_BYTES, -CRC_BYTES);
  let message = null;
  if (type === FRAME_TYPE.DATA) {
    try {
      message = new TextDecoder('utf-8', { fatal: true }).decode(payload);
    } catch {
      return reject('BAD_UTF8', 'payload texte UTF-8 invalide');
    }
  }
  return {
    ok: true,
    type,
    ackRequested: Boolean(flags & 1),
    sequence: bytes[6],
    source: readU16(bytes, 7),
    destination: readU16(bytes, 9),
    payload,
    message,
    checksum: receivedChecksum,
    fingerprint: hex(receivedChecksum),
  };
}

function reject(code, reason) { return { ok: false, code, reason }; }
function hex(value) { return value.toString(16).padStart(8, '0'); }
function assertUint(value, bits, name) {
  const max = 2 ** bits - 1;
  if (!Number.isInteger(value) || value < 0 || value > max) throw new RangeError(`${name} hors plage 0..${max}`);
}
function writeU16(out, offset, value) {
  out[offset] = value >>> 8;
  out[offset + 1] = value;
}
function readU16(input, offset) { return ((input[offset] << 8) | input[offset + 1]) >>> 0; }
function writeU32(out, offset, value) {
  out[offset] = value >>> 24;
  out[offset + 1] = value >>> 16;
  out[offset + 2] = value >>> 8;
  out[offset + 3] = value;
}
function readU32(input, offset) {
  return (((input[offset] << 24) >>> 0) + (input[offset + 1] << 16) + (input[offset + 2] << 8) + input[offset + 3]) >>> 0;
}
function toBase64Url(bytes) {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '');
}
function fromBase64Url(value) {
  if (!/^[A-Za-z0-9_-]+$/u.test(value)) throw new Error('alphabet invalide');
  const padded = value.replaceAll('-', '+').replaceAll('_', '/') + '='.repeat((4 - value.length % 4) % 4);
  const binary = atob(padded);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}
